/**
 * ResetPassword page — set a new password using the reset token.
 */

import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { apiFetch, getNetworkErrorMessage } from '../../lib/api';
import { AuthLayout } from './AuthLayout';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token.');
    }
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await apiFetch('/v1/auth/reset-password', {
        method: 'POST',
        body: { token, password },
      });
      setSuccess(true);
      addToast({
        type: 'success',
        message: 'Password reset successfully!',
      });
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(getNetworkErrorMessage(err, 'Failed to reset password.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Reset Password" subtitle="Create a new password for your account">
      {success ? (
        <div className="space-y-4 text-center">
          <div className="flex items-center justify-center text-4xl text-emerald-400">✓</div>
          <p className="text-sm text-slate-300">
            Your password has been reset successfully! Redirecting you to login...
          </p>
        </div>
      ) : error && !token ? (
        <div className="space-y-4 text-center">
          <p className="text-sm text-red-300">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            Request a new reset link
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="reset-password"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-400"
            >
              New Password
            </label>
            <input
              id="reset-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label
              htmlFor="reset-password-confirm"
              className="block text-xs font-semibold uppercase tracking-wider text-slate-400"
            >
              Confirm New Password
            </label>
            <input
              id="reset-password-confirm"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-2 w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              disabled={isSubmitting}
              required
            />
          </div>

          {error && (
            <p className="rounded-lg border border-red-800 bg-red-950/30 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !password || !confirmPassword}
            className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white shadow-lg transition duration-200 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" /> Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
